<template>
  <div class="rms-reservation-calendar min-w-0 overflow-hidden" data-testid="reservation-calendar" role="region" :aria-label="monthTitle">
    <div class="flex items-center justify-between gap-2 sm:gap-4 mb-4 sm:mb-5">
      <h3 class="text-lg sm:text-xl font-semibold text-charcoal dark:text-white truncate min-w-0">
        {{ monthTitle }}
      </h3>
      <div class="flex shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <button
          type="button"
          class="min-h-[44px] min-w-[44px] flex items-center justify-center text-charcoal dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
          :aria-label="$t('app.reservationsCalendarPrev')"
          @click="prevMonth"
        >
          <span class="material-icons text-xl">chevron_left</span>
        </button>
        <div class="w-px bg-slate-200 dark:bg-slate-700 shrink-0" aria-hidden="true" />
        <button
          type="button"
          class="min-h-[44px] min-w-[44px] flex items-center justify-center text-charcoal dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
          :aria-label="$t('app.reservationsCalendarNext')"
          @click="nextMonth"
        >
          <span class="material-icons text-xl">chevron_right</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-12 text-center text-slate-500 dark:text-slate-400">
      {{ $t('app.loading') }}
    </div>

    <div v-else class="pt-3 sm:pt-4">
      <div ref="calendarContainerRef" class="rms-tui-calendar rms-tui-calendar--touch" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import Calendar from '@toast-ui/calendar'
import '@toast-ui/calendar/dist/toastui-calendar.min.css'
import { formatYmd } from '@/utils/date'

const { t } = useI18n()
const props = defineProps({
  /** Current month as YYYY-MM */
  month: { type: String, required: true },
  /** List of reservations (items with booking_date, booking_time, uuid, customer_name, etc.) */
  reservations: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  /** Selected date Y-m-d for highlight (optional; TUI Calendar doesn't need it for day modal) */
  selectedDate: { type: String, default: null },
})

const emit = defineEmits(['update:month', 'day-selected'])

const calendarContainerRef = ref(null)
/** @type {import('@toast-ui/calendar').default | null} */
let calendarInstance = null
/** @type {((e: Event) => void) | null} */
let boundGridClickHandler = null

const CALENDAR_ID = 'reservations'

const monthTitle = computed(() => {
  const [y, m] = props.month.split('-').map(Number)
  const d = new Date(y, m - 1, 1)
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
})

/** Primary color for calendar (matches app Tailwind primary). */
const CALENDAR_PRIMARY = '#e85a2b'
const CALENDAR_PRIMARY_DARK = '#c94d1f'

/** Convert one reservation to Toast UI Calendar EventObject */
function reservationToEvent(res) {
  const date = res.booking_date ?? res.date
  const time = res.booking_time ?? '12:00'
  if (!date) return null
  const [y, mo, d] = date.split('-').map(Number)
  const [h, min] = time.split(':').map(Number)
  const start = new Date(y, mo - 1, d, h || 0, min || 0)
  const end = new Date(start.getTime() + 60 * 60 * 1000) // +1h
  return {
    id: res.uuid,
    calendarId: CALENDAR_ID,
    title: `${res.customer_name || t('app.reservationsTableCustomer')} · ${time}`,
    start,
    end,
    category: 'time',
    raw: res,
  }
}

/** Map of date (YYYY-MM-DD) -> count of reservations for that date. */
function getCountByDate() {
  const byDate = new Map()
  for (const res of props.reservations) {
    const date = res.booking_date ?? res.date
    if (!date) continue
    byDate.set(date, (byDate.get(date) ?? 0) + 1)
  }
  return byDate
}

/** Build calendar events: one per reservation for days with 1 res; one "N reservations" for days with 2+. */
function buildCalendarEvents() {
  const list = props.reservations
  const byDate = new Map()
  for (const res of list) {
    const date = res.booking_date ?? res.date
    if (!date) continue
    if (!byDate.has(date)) byDate.set(date, [])
    byDate.get(date).push(res)
  }
  const events = []
  for (const [ymd, ress] of byDate) {
    if (ress.length >= 2) {
      const [y, mo, d] = ymd.split('-').map(Number)
      const start = new Date(y, mo - 1, d, 0, 0)
      const end = new Date(y, mo - 1, d, 23, 59)
      events.push({
        id: `agg-${ymd}`,
        calendarId: CALENDAR_ID,
        title: `${ress.length} ${t('app.reservationsCount')}`,
        start,
        end,
        category: 'allday',
        raw: null,
      })
    } else if (ress.length === 1) {
      const ev = reservationToEvent(ress[0])
      if (ev) events.push(ev)
    }
  }
  return events
}

/** Find day cell from event target and emit day-selected (fallback when Toast UI doesn't fire selectDateTime on touch). */
function handleGridCellTap(container, event) {
  if (!calendarInstance) return
  // On touch, use coordinates so we hit-test the element under the finger (event.target can be wrong on touchend).
  const el =
    event.type === 'touchend' && event.changedTouches?.[0]
      ? document.elementFromPoint(event.changedTouches[0].clientX, event.changedTouches[0].clientY)
      : event.target
  if (!el || !container.contains(el)) return
  const cell = el.closest('.toastui-calendar-daygrid-cell')
  if (!cell) return
  const d = calendarInstance.getDate()
  const year = d.getFullYear()
  const month = d.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  // Prefer reading the displayed day number from the cell (badge may be inside dateEl, so take only first 1–2 digits).
  const dateEl = cell.querySelector('[class*="grid-cell-date"], [class*="weekday-grid-date"]')
  const dayText = dateEl?.textContent?.trim().match(/^\d{1,2}/)?.[0]
  const dayOfMonth = dayText ? parseInt(dayText, 10) : null

  if (dayOfMonth != null && dayOfMonth >= 1 && dayOfMonth <= daysInMonth) {
    emit('day-selected', formatYmd(new Date(year, month, dayOfMonth)))
    return
  }

  // Fallback: compute from row/column (cell may be in previous/next month or structure differs).
  const row = cell.parentElement
  if (!row) return
  const rows = Array.from(row.parentElement?.children ?? [])
  const rowIndex = rows.indexOf(row)
  const colIndex = Array.from(row.children).indexOf(cell)
  if (rowIndex < 0 || colIndex < 0) return
  const cellIndex = rowIndex * 7 + colIndex
  const firstDay = new Date(year, month, 1).getDay()
  const fallbackDay = cellIndex - firstDay + 1
  if (fallbackDay < 1 || fallbackDay > daysInMonth) return
  emit('day-selected', formatYmd(new Date(year, month, fallbackDay)))
}

function initCalendar() {
  if (!calendarContainerRef.value) return
  calendarInstance = new Calendar(calendarContainerRef.value, {
    defaultView: 'month',
    useFormPopup: false,
    useDetailPopup: false,
    isReadOnly: true,
    usageStatistics: false,
    gridSelection: {
      enableClick: true,
      enableDblClick: false,
    },
    month: {
      visibleWeeksCount: 0,
      dayNames: [
        t('app.weekdaySun'),
        t('app.weekdayMon'),
        t('app.weekdayTue'),
        t('app.weekdayWed'),
        t('app.weekdayThu'),
        t('app.weekdayFri'),
        t('app.weekdaySat'),
      ],
    },
    calendars: [
      {
        id: CALENDAR_ID,
        name: t('app.reservations'),
        color: CALENDAR_PRIMARY,
        backgroundColor: CALENDAR_PRIMARY,
        borderColor: CALENDAR_PRIMARY_DARK,
        dragBackgroundColor: CALENDAR_PRIMARY_DARK,
      },
    ],
  })

  calendarInstance.on('selectDateTime', (info) => {
    const ymd = formatYmd(info.start)
    emit('day-selected', ymd)
  })

  // Do not open day modal when clicking the event bar; only the date cell is clickable (selectDateTime / handleGridCellTap).

  // Set initial date to props.month
  const [y, m] = props.month.split('-').map(Number)
  calendarInstance.setDate(new Date(y, m - 1, 1))
  syncEvents()
  calendarInstance.render()
  nextTick(injectDayCounts)

  // Fallback for mobile: Toast UI often doesn't fire selectDateTime on touch. Delegate tap/click and derive date from cell.
  const container = calendarContainerRef.value
  if (container) {
    boundGridClickHandler = handleGridCellTap.bind(null, container)
    container.addEventListener('click', boundGridClickHandler, { passive: true })
    container.addEventListener('touchend', boundGridClickHandler, { passive: true })
  }
}

function syncEvents() {
  if (!calendarInstance) return
  calendarInstance.clear()
  const events = buildCalendarEvents()
  if (events.length) {
    calendarInstance.createEvents(events)
  }
}

/** Inject reservation-count badges into day cells. Run after calendar render (e.g. nextTick after syncEvents). */
function injectDayCounts() {
  const container = calendarContainerRef.value
  if (!container) return
  const [year, month] = props.month.split('-').map(Number)
  const firstDay = new Date(year, month - 1, 1).getDay()
  const lastDay = new Date(year, month, 0).getDate()
  const countByDate = getCountByDate()
  const cells = container.querySelectorAll('.toastui-calendar-daygrid-cell')
  cells.forEach((cell, i) => {
    const dayOfMonth = i - firstDay + 1
    if (dayOfMonth < 1 || dayOfMonth > lastDay) return
    const ymd = `${year}-${String(month).padStart(2, '0')}-${String(dayOfMonth).padStart(2, '0')}`
    const count = countByDate.get(ymd)
    const existing = cell.querySelector('.rms-day-count')
    if (existing) existing.remove()
    if (count == null || count === 0) return
    const badge = document.createElement('span')
    badge.className = 'rms-day-count'
    badge.setAttribute('aria-hidden', 'true')
    badge.textContent = String(count)
    const dateWrap = cell.querySelector('[class*="grid-cell-date"], [class*="weekday-grid-date"]')
    if (dateWrap) {
      dateWrap.appendChild(badge)
    } else {
      cell.appendChild(badge)
    }
  })
}

function prevMonth() {
  if (!calendarInstance) return
  calendarInstance.prev()
  const d = calendarInstance.getDate()
  const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  emit('update:month', next)
}

function nextMonth() {
  if (!calendarInstance) return
  calendarInstance.next()
  const d = calendarInstance.getDate()
  const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  emit('update:month', next)
}

onMounted(() => {
  if (!props.loading) {
    requestAnimationFrame(() => {
      initCalendar()
    })
  }
})

function removeGridClickListener() {
  const container = calendarContainerRef.value
  if (container && boundGridClickHandler) {
    container.removeEventListener('click', boundGridClickHandler)
    container.removeEventListener('touchend', boundGridClickHandler)
    boundGridClickHandler = null
  }
}

onBeforeUnmount(() => {
  removeGridClickListener()
  if (calendarInstance) {
    calendarInstance.destroy()
    calendarInstance = null
  }
})

watch(
  () => props.loading,
  async (loading) => {
    if (loading) {
      // Container is removed by v-if; destroy calendar so we re-init when loading becomes false
      removeGridClickListener()
      if (calendarInstance) {
        calendarInstance.destroy()
        calendarInstance = null
      }
      return
    }
    await nextTick()
    requestAnimationFrame(() => {
      if (!calendarInstance && calendarContainerRef.value) {
        initCalendar()
      } else if (calendarInstance) {
        syncEvents()
        nextTick(injectDayCounts)
      }
    })
  }
)

watch(
  () => props.reservations,
  () => {
    if (calendarInstance) {
      syncEvents()
      nextTick(injectDayCounts)
    }
  },
  { deep: true }
)

watch(
  () => props.month,
  (month) => {
    if (!calendarInstance) return
    const [y, m] = month.split('-').map(Number)
    calendarInstance.setDate(new Date(y, m - 1, 1))
    syncEvents()
    nextTick(injectDayCounts)
  }
)
</script>

<style>
/* Toast UI floating layer can be mounted outside our component. Disable on mobile so taps reach the calendar. */
@media (max-width: 639px) {
  .toastui-calendar-floating-layer {
    pointer-events: none !important;
    visibility: hidden !important;
  }
}
</style>

<style scoped>
.rms-tui-calendar {
  width: 100%;
  height: 360px;
  min-height: 360px;
  touch-action: manipulation; /* remove 300ms tap delay on mobile */
}
.rms-tui-calendar--touch :deep([class*="daygrid-cell"]),
.rms-tui-calendar--touch :deep([class*="grid-cell-date"]) {
  cursor: pointer;
}
@media (min-width: 640px) {
  .rms-tui-calendar {
    height: 420px;
    min-height: 420px;
  }
}
/* Day names: font-medium, text-sm (sm+); mobile overridden below */
:deep(.toastui-calendar-template-monthDayName),
:deep(.toastui-calendar-day-name-item) {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--tw-slate-600, #475569);
}
:deep(.dark .toastui-calendar-template-monthDayName),
:deep(.dark .toastui-calendar-day-name-item) {
  color: var(--tw-slate-400, #94a3b8);
}
/* Date numbers: font-medium */
:deep(.toastui-calendar-grid-cell-date),
:deep(.toastui-calendar-weekday-grid-date) {
  font-weight: 500;
}
/* Today: primary color (matches app primary #e85a2b) */
:deep(.toastui-calendar-weekday-grid-date.today),
:deep(.toastui-calendar-weekday-grid-date-decorator.today) {
  background-color: rgb(232 90 43 / 0.1);
  --tw-ring-color: #e85a2b;
  box-shadow: 0 0 0 1px var(--tw-ring-color);
  font-weight: 600;
}
:deep(.dark .toastui-calendar-weekday-grid-date.today),
:deep(.dark .toastui-calendar-weekday-grid-date-decorator.today) {
  background-color: rgb(232 90 43 / 0.2);
  --tw-ring-color: #e85a2b;
}
/* Hide "N more" overflow label – we use day modal for the list; no separate "more" link */
:deep(.toastui-calendar-grid-cell-more-events),
:deep(.toastui-calendar-more-events) {
  display: none !important;
}
/* Only show badge + count for reservations; hide Toast UI event bars */
:deep(.toastui-calendar-weekday-events) {
  display: none !important;
}
/* Reservation count badge (injected into day cells) */
:deep([class*="grid-cell-date"]:has(.rms-day-count)),
:deep([class*="weekday-grid-date"]:has(.rms-day-count)) {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}
:deep(.rms-day-count) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0.25rem 0.375rem;
  margin-top: 2px;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  background-color: #e85a2b;
  border-radius: 9999px;
  pointer-events: none;
}
:deep(.dark .rms-day-count) {
  background-color: #f07a4b;
  color: #fff;
}
/* Ensure Toast UI Calendar root fills container (it uses height: 100%) */
:deep(.toastui-calendar-layout) {
  width: 100%;
  height: 100%;
}

/* Mobile: improve readability and tap targets */
@media (max-width: 639px) {
  :deep(.toastui-calendar-month .toastui-calendar-day-names) {
    height: 28px;
    padding: 0 4px;
    font-size: 0.75rem;
  }
  :deep(.toastui-calendar-day-name-item.toastui-calendar-month) {
    height: 28px;
    line-height: 28px;
    padding: 0 2px;
    font-size: 0.75rem;
  }
  :deep(.toastui-calendar-daygrid-cell) {
    padding: 2px 0;
  }
  :deep(.toastui-calendar-grid-cell-date) {
    min-height: 28px;
    min-width: 28px;
    height: 28px;
    width: 28px;
    line-height: 28px;
    font-size: 13px;
  }
  :deep(.toastui-calendar-grid-cell-date .toastui-calendar-weekday-grid-date.toastui-calendar-weekday-grid-date-decorator) {
    height: 24px;
    width: 24px;
    line-height: 24px;
    font-size: 12px;
    margin-left: 1px;
  }
  /* Hide Toast UI's built-in popup and make the floating layer not block taps so day-selected still fires */
  :deep(.toastui-calendar-see-more-container),
  :deep(.toastui-calendar-detail-container),
  :deep(.toastui-calendar-floating-layer .toastui-calendar-popup-container) {
    display: none !important;
  }
  :deep(.toastui-calendar-floating-layer) {
    pointer-events: none !important;
    visibility: hidden !important;
  }
}
</style>
